import type { DocumentReadKind } from '../api/documents'
import type { OperatorPortalConfig } from './operatorPortalConfig'

/**
 * Riquadri dei documenti della «Nuova pratica». Le pratiche energia hanno due riquadri per la bolletta, luce e gas
 * (2/11/2026: con una sola bolletta la lettura non trovava i dati dell'altra fornitura). Le due bollette si leggono
 * allo stesso modo e si allegano entrambe come «bolletta»: su Ermes finiscono nella casella Bolletta, una dopo l'altra.
 */
export type DocSlotKey = DocumentReadKind | 'bolletta_gas'

export interface DocSlotDef {
  slot: DocSlotKey
  /** Tipo di lettura e tipo del documento allegato. */
  kind: DocumentReadKind
  label: string
  /** Prefisso del nome del file allegato quando un altro riquadro ha un file con lo stesso nome. */
  prefix: string
  /** Campi mostrati dopo la lettura, da rileggere a mano. */
  fields: [string, string][]
  /** Campi mostrati solo se la lettura li ha trovati (luce e gas nella stessa bolletta). */
  extra?: [string, string][]
}

const BILL_COMMON: [string, string][] = [
  ['intestatario', 'Intestatario'], ['indirizzoIntestatario', 'Indirizzo di intestazione'], ['indirizzoFornitura', 'Indirizzo di fornitura'],
  ['fornitore', 'Fornitore attuale'], ['numeroFattura', 'Numero fattura'], ['importo', 'Importo'], ['dataEmissione', 'Data emissione'],
  ['distributore', 'Distributore di rete'],
]
const BILL_LUCE: [string, string][] = [
  ['pod', 'POD'], ['potenzaImpegnata', 'Potenza impegnata (kW)'], ['potenzaDisponibile', 'Potenza disponibile (kW)'],
  ['consumoAnnuo', 'Consumo annuo luce (kWh)'], ['tensione', 'Tensione (BT/MT)'],
]
const BILL_GAS: [string, string][] = [['pdr', 'PDR'], ['consumoAnnuoGas', 'Consumo annuo gas (Smc)']]

export const DOC_SLOTS: DocSlotDef[] = [
  {
    slot: 'carta_identita', kind: 'carta_identita', label: "Carta d'identità", prefix: 'documento',
    fields: [['nome', 'Nome'], ['cognome', 'Cognome'], ['codiceFiscale', 'Codice fiscale'], ['dataNascita', 'Data di nascita'],
      ['luogoNascita', 'Luogo di nascita'], ['numeroDocumento', 'Numero documento'], ['scadenza', 'Scadenza'], ['indirizzo', 'Indirizzo']],
  },
  { slot: 'bolletta', kind: 'bolletta', label: 'Bolletta luce', prefix: 'luce', fields: [...BILL_COMMON, ...BILL_LUCE], extra: BILL_GAS },
  { slot: 'bolletta_gas', kind: 'bolletta', label: 'Bolletta gas', prefix: 'gas', fields: [...BILL_COMMON, ...BILL_GAS], extra: BILL_LUCE },
  {
    slot: 'visura', kind: 'visura', label: 'Visura camerale', prefix: 'visura',
    fields: [['ragioneSociale', 'Ragione sociale'], ['partitaIva', 'Partita IVA'], ['codiceFiscale', 'Codice fiscale'],
      ['sedeLegale', 'Sede legale'], ['formaGiuridica', 'Forma giuridica'], ['pec', 'PEC'], ['codiceAteco', 'Codice ATECO']],
  },
]

/**
 * Riquadri chiesti dal portale dell'operatore: per l'energia bolletta luce e bolletta gas, per gli altri portali una
 * sola «Bolletta» con tutti i campi.
 */
export function docSlotsFor(config: Pick<OperatorPortalConfig, 'kind' | 'documents'>): DocSlotDef[] {
  const energia = config.kind === 'energia'
  return DOC_SLOTS
    .filter(d => config.documents.includes(d.kind) && (d.slot !== 'bolletta_gas' || energia))
    .map(d => {
      if (d.slot !== 'bolletta' || energia) return d
      const { extra = [], ...rest } = d
      return { ...rest, label: 'Bolletta', prefix: 'bolletta', fields: [...d.fields, ...extra] }
    })
}

/**
 * Campi da mostrare dopo la lettura: quelli del riquadro più quelli dell'altra fornitura che la lettura ha trovato
 * (`read` = i valori letti, non quelli corretti a mano: un campo svuotato resta visibile).
 */
export function visibleFields(def: DocSlotDef, read: Record<string, string>): [string, string][] {
  return [...def.fields, ...(def.extra ?? []).filter(([name]) => !!read[name]?.trim())]
}

/** Unisce letture dello stesso tipo: vince il primo valore non vuoto (la bolletta luce prima di quella gas). */
export function mergeReads(...reads: Record<string, string>[]): Record<string, string> {
  const out: Record<string, string> = {}
  for (const r of reads) for (const [k, v] of Object.entries(r)) if (v?.trim() && !out[k]?.trim()) out[k] = v
  return out
}

/**
 * Il file come va allegato: se un altro riquadro ha un file con lo stesso nome (es. due «fattura.pdf»), questo prende
 * il prefisso del riquadro («gas-fattura.pdf»). Su Ermes il CRM riconosce i file già caricati dal nome: due nomi
 * uguali farebbero saltare la seconda bolletta a un nuovo caricamento.
 */
export function distinctUploadFile(file: File, prefix: string, others: (File | null | undefined)[]): File {
  const same = others.some(o => !!o && o !== file && o.name.toLowerCase() === file.name.toLowerCase())
  return same ? new File([file], `${prefix}-${file.name}`, { type: file.type, lastModified: file.lastModified }) : file
}
